import { Injectable } from '@angular/core';
import { RestfulEndpoint } from '../restful-endpoint.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import {
  ResponseObservationData,
  ObservationCategory,
  ObservationStatus,
  ObservationDetailsResponse,
  ObservationFilterResponse,
  ObservationParamsOptions,
  ObservationRefactor,
  TopUserData,
  ObservationListRequestBody,
} from '../../models/observations.model';
import { ProjectActiveMembers } from '../../models/members.model';
import { Observation } from '../../models/project.model';
import { Watchers } from '../../models/watchers.model';
import { GStatic } from '../GStatic';
import { ObservationElement } from '../../modules/p-dashboard/observation-management/observation-management.component';
import { TranslocoService } from '@ngneat/transloco';
import { XlsxService } from '../xlsx.service';
import { createExportId, ObservationExportFormat } from '../user-activity.service';
import { AccessLevel } from '../../core/user/user.model';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { AnalyticsService } from '../Analytics/analytics.service';
import { ToastrService } from 'ngx-toastr';
import {
  formDataWithObservationHashtagsStrippedForApi,
  normalizeObservationFilterHashtagsForV2,
  stripHashtagPrefixForApi,
} from '../../shared/utils/observation-form.utils';

export interface ExportProgressState {
  active: boolean;
  currentPage: number;
  totalPages: number | null;
  processedRows: number;
  totalItems: number | null;
  cappedByTestMode: boolean;
}

export interface ObservationExportSelection {
  selectAll?: boolean;
  selectedObservations?: Array<{ id?: string }>;
  // Rows excluded while select-all is active; exported as deselected_ids.
  deselectedObservations?: Array<{ id?: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class ObservationsService {
  // Region: Properties

  _observationFilter: ObservationParamsOptions = {} as ObservationParamsOptions;
  observations: ObservationRefactor[] = [];
  per_page: number = 20;
  page: number = 1;
  barChart: any;
  userChart: any;
  pieChart: any;
  observationsAreLoading: boolean;
  observationFilterDefaultData: ObservationFilterResponse;
  private _pageIndex = new BehaviorSubject<number>(0);
  pageIndex$ = this._pageIndex.asObservable();
  observationsSummaryReport: any;
  isLoading: boolean = false;
  readonly exportProgress$ = new BehaviorSubject<ExportProgressState>({
    active: false,
    currentPage: 0,
    totalPages: null,
    processedRows: 0,
    totalItems: null,
    cappedByTestMode: false,
  });

  // Region: Constructor
  sort_by: string = 'created_at';
  sort_dir: string = 'desc';
  over_due_only: '1' | '0' = '0';

  // Region: Constructor

  constructor(
    private endPoint: RestfulEndpoint,
    private translateService: TranslocoService,
    private xlsxService: XlsxService,
    private _router: Router,
    private analyticsService: AnalyticsService,
    private _toaster: ToastrService,
  ) {
    this._observationFilter.categories = [];
  }

  // Region: Public Methods

  // Observation Categories
  observationCategories(
    observationType?: string,
  ): Observable<{ observation_categories: ObservationCategory[] }> {
    const body = observationType ? { type: observationType } : {};
    return this.endPoint.post('get_observation_categories', body);
  }

  // Observation Statuses
  observationStatuses(
    observationType?: string,
  ): Observable<{ observation_statuses: ObservationStatus[] }> {
    const body = observationType ? { type: observationType } : {};
    return this.endPoint.post('get_observation_statuses', body);
  }

  /**
   * Validates if the provided status is one of the available statuses for the given observation type
   * @param status - The status value to validate
   * @param availableStatuses - Array of available statuses
   * @returns boolean - true if status is valid, false otherwise
   */
  validateObservationStatus(status: string, availableStatuses: ObservationStatus[]): boolean {
    if (!status || !availableStatuses || availableStatuses.length === 0) {
      return false;
    }

    return availableStatuses.some((availableStatus) => availableStatus.value === status);
  }

  // Observation Categories
  observationSignificancesAndTypes(): Observable<any> {
    return this.endPoint.get('observations/init-form');
  }

  // Permission Methods
  getPermissionObservationManagement(project) {
    return project.getPermission(AccessLevel.OBSERVATION_MANAGEMENT);
  }

  getObservationManagerPermission(project) {
    return project.getPermission(AccessLevel.OPEN_OBSERVATIONS_MANAGEMENT);
  }

  getOpenObservationsPermission(project) {
    return project.getPermission(AccessLevel.OPEN_OBSERVATIONS);
  }

  getObservationAnalyticsPermission(project): boolean {
    return project.getPermission(AccessLevel.SHOW_HIDE_OBSERVATIONS_ANALYTICS);
  }

  getExportExcelPermission(project): boolean {
    return project.getPermission(AccessLevel.EXPORT_TO_EXCEL);
  }

  getObservationSummaryPermission(project): boolean {
    return project.getPermission(AccessLevel.GENERATE_OBSERVATION_SUMMARY_REPORT);
  }

  getSearchByIdPermission(project): boolean {
    return project.getPermission(AccessLevel.SEARCH_BY_ID);
  }

  getObservationFilterPermission(project): boolean {
    return project.getPermission(AccessLevel.FILTER_OBSERVATIONS);
  }

  // Project Active Members
  projectActiveMembers(project_id: string): Observable<ProjectActiveMembers> {
    return this.endPoint.post('get_project_active_members', { project_id });
  }

  // Chart Data
  topUserData(project_id: string, dateParams?: any): Observable<TopUserData> {
    return this.endPoint.post('users_have_high_number_of_observations', {
      project_id,
      ...dateParams,
    });
  }

  // Update Observation (hashtags must be sent without leading `#` for API)
  updateObservation(body: any): Observable<ObservationDetailsResponse> {
    if (body instanceof FormData) {
      return this.endPoint.post(
        'update_observation',
        formDataWithObservationHashtagsStrippedForApi(body),
      );
    }
    if (body && typeof body === 'object' && Array.isArray(body.hashtags)) {
      const hashtags = body.hashtags
        .map((t: unknown) => stripHashtagPrefixForApi(t))
        .filter((t: string) => t.length > 0);
      return this.endPoint.post('update_observation', { ...body, hashtags });
    }
    return this.endPoint.post('update_observation', body);
  }

  // Get Observations
  getObservations(
    body: ObservationListRequestBody,
  ): Observable<{ observation_list: ObservationRefactor[]; meta_data?: unknown }> {
    return this.endPoint.post('get_observations', body);
  }

  // Get Mentionable Users
  getMentionableUsers(body: any): Observable<Watchers[]> {
    return this.endPoint.post('users-who-could-be-mentioned-on-observation', body);
  }

  // Get Observation Watchers
  getObservationWatchers(body: any): Observable<Watchers[]> {
    return this.endPoint.post('observation-watchers', body);
  }

  // Get Observation Logs
  getObservationLogs(observation_id: string): Observable<{ observation_list: Observation[] }> {
    return this.endPoint.post('observation_history', { observation_id });
  }

  // Get Observation Details
  getObservationDetails(observation_id: string): Observable<ObservationDetailsResponse> {
    return this.endPoint.post('get_observation_details', { observation_id });
  }

  // Trigger outbound sync for an observation
  syncObservationOutbound(
    observationId: string,
    projectId: string,
  ): Observable<{ message: string }> {
    // Absolute path via RestfulEndpoint: environment.api_base_url already prefixes '/api'
    // Using get with explicit version null to avoid adding '/v2/' and pass full path
    return this.endPoint.post(
      `integration/projects/${projectId}/observations/${observationId}/outbound-sync`,
      {},
      undefined,
      undefined,
    );
  }

  // Create Observation
  createObservation(body: any): Observable<any> {
    return this.endPoint.post('create_observation', body);
  }

  // List all observation hashtags
  getObservationHashtagsAll(projectId?: string): Observable<any> {
    return this.endPoint.get('observations/hashtags', {
      project_id: projectId || undefined,
      is_all: 1,
      sort_by: 'created_at',
      sort_dir: 'desc',
    });
  }

  // Create Observation Reply
  createObservationReply(body: any): any {
    return this.endPoint.post('create_reply_on_observation', body);
  }

  // Initialize Observation Filter
  initObservationFilter(projectId?: string): Observable<ObservationFilterResponse> {
    const body = projectId ? { project_id: projectId } : {};
    return this.endPoint.post('init_observations_filter', body);
  }

  // Reset Observation Filter
  resetObservationFilter(): void {
    this._observationFilter = {} as ObservationParamsOptions;
    const urlWithoutQueryParams = this._router.url.split('?')[0];
    this._router.navigateByUrl(urlWithoutQueryParams);
  }

  // Export Excel Sheet
  exportExcel(body: any): Observable<any> {
    return this.endPoint.post('export_excel', body);
  }

  async fetchExportPages(
    body: any,
    perPage: number,
    onPageFetched: (rows: any[], meta: any) => Promise<void> | void,
    maxPages?: number,
  ): Promise<{ cappedByMaxPages: boolean; fetchedPages: number }> {
    let currentPage = 1;
    let hasMore = true;
    let fetchedPages = 0;
    let cappedByMaxPages = false;

    while (hasMore) {
      if (maxPages && currentPage > maxPages) {
        cappedByMaxPages = true;
        break;
      }

      const response = await firstValueFrom(
        this.exportExcel({
          ...body,
          page: currentPage,
          per_page: perPage,
        }),
      );

      const rows = response?.observation_list || [];
      const meta = response?.meta_data || {
        current_page: currentPage,
        per_page: perPage,
        total_pages: currentPage,
        total_items: rows.length,
        has_more: false,
      };

      await onPageFetched(rows, meta);
      fetchedPages += 1;

      hasMore = Boolean(meta?.has_more);
      currentPage += 1;

      if (!rows.length && !hasMore) {
        break;
      }
    }

    return { cappedByMaxPages, fetchedPages };
  }

  // Delete Observation
  deleteObservation(body: { observation_id: string; project_id: string }): Observable<any> {
    return this.endPoint.post('delete_observation', body);
  }

  // Get Observations Refactored
  getObservationsRefactored(project_id?: string): Observable<ResponseObservationData> {
    this.observationsAreLoading = true;

    // The list endpoint is always paginated. Keep safe defaults here because this
    // service is shared across several screens and can be called before a component
    // restores pagination from its route.
    this.per_page = this.normalizePaginationValue(this.per_page, 20);
    this.page = this.normalizePaginationValue(this.page, 1);

    // Conditionally set or remove project_id from the observation filter
    if (project_id) {
      this._observationFilter.project_id = project_id;
    } else {
      delete this._observationFilter.project_id;
    }

    this._observationFilter.sort_by = this.sort_by;
    this._observationFilter.sort_dir = this.sort_dir;
    this._observationFilter.over_due_only = this.over_due_only;
    this._observationFilter.per_page = this.per_page;
    this._observationFilter.page = this.page;

    // Transform array parameters (categories, created_by, floors) from indexed format to proper arrays
    const transformedFilter = this.transformFilterArrayParameters(this._observationFilter);

    // Clean the filter object before sending; v2 expects hashtags without leading #
    const cleanedFilter = normalizeObservationFilterHashtagsForV2(
      this.cleanObjectProperties(transformedFilter) as Record<string, unknown>,
    );

    // Delay to apply query params to the URL for navigation purposes
    setTimeout(() => {
      if (cleanedFilter) {
        this._router.navigate([], {
          queryParams: this._observationFilter, // Keep original format for URL
          queryParamsHandling: 'merge',
        });
      }
    }, 500);

    this.observationsAreLoading = false;

    // Call the API with POST request, sending parameters in the body
    return this.endPoint.post('v2/observations/list', cleanedFilter || {});
  }

  private normalizePaginationValue(value: number, fallback: number): number {
    return Number.isInteger(value) && value > 0 ? value : fallback;
  }

  getObservationsWithFilter(filter: ObservationParamsOptions): Observable<ResponseObservationData> {
    const paginatedFilter = {
      ...filter,
      page: this.normalizePaginationValue(filter?.page, 1),
      per_page: this.normalizePaginationValue(filter?.per_page, 20),
    };

    // Transform array parameters (categories, created_by, floors) from indexed format to proper arrays
    const transformedFilter = this.transformFilterArrayParameters(paginatedFilter);

    const cleanedFilter = normalizeObservationFilterHashtagsForV2(
      this.cleanObjectProperties(transformedFilter) as Record<string, unknown>,
    );
    return this.endPoint.post('v2/observations/list', cleanedFilter || {});
  }

  /**
   * Transforms indexed array parameters (e.g., key[0], key[1], key[2])
   * into proper array format (key: [value0, value1, value2])
   * Works for any parameter with [] notation, making it future-proof
   */
  transformFilterArrayParameters(filter: any): any {
    const result = {};
    const arrayParams: { [key: string]: any[] } = {};

    // Pattern to match array notation: key[index]
    const arrayPattern = /^(.+)\[(\d+)\]$/;

    Object.keys(filter).forEach((key) => {
      const match = key.match(arrayPattern);

      if (match) {
        // This is an array parameter (e.g., categories[0])
        const baseKey = match[1]; // e.g., "categories"
        const index = parseInt(match[2], 10); // e.g., 0

        // Initialize array for this key if not exists
        if (!arrayParams[baseKey]) {
          arrayParams[baseKey] = [];
        }

        // Add value to the array (only if not null/undefined)
        const value = filter[key];
        if (value !== undefined && value !== null) {
          arrayParams[baseKey][index] = value;
        }
      } else {
        // This is a regular parameter, copy as-is
        result[key] = filter[key];
      }
    });

    // Add all array parameters to result, filtering out empty/undefined values
    Object.keys(arrayParams).forEach((baseKey) => {
      const cleanedArray = arrayParams[baseKey].filter(
        (value) => value !== undefined && value !== null,
      );
      if (cleanedArray.length > 0) {
        result[baseKey] = cleanedArray;
      }
    });

    return result;
  }

  cleanObjectProperties(objectToClean) {
    return _(objectToClean)
      .pickBy(_.identity)
      .omitBy(_.isUndefined)
      .omitBy(_.isNull)
      .omitBy((value) => value === 'undefined')
      .omitBy((value) => value === 'null')
      .value();
  }

  /**
   * The current list filters in the shape the list request sends them, without paging.
   * Used as the audit filters of the Summary PDF, which reads its rows from the list.
   */
  buildCurrentExportFilters(): Record<string, unknown> {
    const transformedFilter = this.transformFilterArrayParameters(this._observationFilter || {});
    const filters = normalizeObservationFilterHashtagsForV2(
      this.cleanObjectProperties(transformedFilter || {}) as Record<string, unknown>,
    );
    delete filters['page'];
    delete filters['per_page'];
    return filters;
  }

  private resolveExportFormat(
    type: 'xlsx' | 'csv',
    includeImages: boolean,
  ): ObservationExportFormat {
    if (type === 'csv') {
      return 'csv';
    }
    return includeImages ? 'excel_with_images' : 'excel_without_images';
  }

  async exportSheet(
    type: 'xlsx' | 'csv',
    projectId: string,
    includeImages: boolean = true,
    selection?: ObservationExportSelection,
  ): Promise<void> {
    this.isLoading = true;
    this.xlsxService.resetImageErrorLogCount();
    this.exportProgress$.next({
      active: true,
      currentPage: 0,
      totalPages: null,
      processedRows: 0,
      totalItems: null,
      cappedByTestMode: false,
    });
    this._toaster.info(
      this.translateService.translate('Observation Excel is being downloaded in the background', {
        type: type === 'csv' ? 'CSV' : 'Excel',
      }),
    );

    // Build export payload the same way as listing so all filters are preserved,
    // including dynamic array params like categories/created_by/floors and building_id.
    const transformedFilter = this.transformFilterArrayParameters(this._observationFilter || {});
    const observationFilterCopy = normalizeObservationFilterHashtagsForV2(
      this.cleanObjectProperties(transformedFilter || {}) as Record<string, unknown>,
    );
    observationFilterCopy['project_id'] = projectId;

    if (selection?.selectAll) {
      // "Select all across pages": let the backend export everything matching the
      // active filters (paginated via has_more), passing only the rows the user
      // explicitly excluded — instead of enumerating the current page's ids.
      observationFilterCopy['is_all_selected'] = true;
      const deselectedIds = (selection?.deselectedObservations || [])
        .map((observation) => observation?.id)
        .filter((id): id is string => Boolean(id));
      if (deselectedIds.length > 0) {
        observationFilterCopy['deselected_ids'] = deselectedIds;
      }
    } else {
      // Hand-picked subset: export exactly the selected ids.
      const selectedObservationIds = (selection?.selectedObservations || [])
        .map((observation) => observation?.id)
        .filter((id): id is string => Boolean(id));
      if (selectedObservationIds.length > 0) {
        observationFilterCopy['ids'] = selectedObservationIds;
      }
    }

    // One id per press, sent on every page request, so the backend records one audit
    // row for the whole export. Both fields are optional for the backend.
    const exportId = createExportId();
    if (exportId) {
      observationFilterCopy['export_id'] = exportId;
    }
    observationFilterCopy['export_format'] = this.resolveExportFormat(type, includeImages);

    const lang = this.translateService.getActiveLang();
    const observationsSheetName = this.translateService.translate('All Observations', {}, lang);
    const exportFileName = this.translateService.translate('Observations', {}, lang);
    const exportPerPage = 300;
    const statusTotals = {
      pending: 0,
      in_progress: 0,
      completed: 0,
      closed: 0,
      // Integration-specific statuses (e.g. Unifier's open/approved/rejected/reviewed) are
      // rolled up here so the breakdown sheets always reconcile with the exported rows.
      other: 0,
    };
    const categoryStatusMap = new Map<string, any>();
    const userStatusMap = new Map<string, any>();

    const normalizeStatusKey = (status: unknown): keyof typeof statusTotals => {
      const normalized = `${status || ''}`.trim().toLowerCase().replace(/\s+/g, '_');
      if (normalized === 'in-progress') {
        return 'in_progress';
      }

      if (normalized === 'pending') {
        return 'pending';
      }
      if (normalized === 'in_progress') {
        return 'in_progress';
      }
      if (normalized === 'completed') {
        return 'completed';
      }
      if (normalized === 'closed') {
        return 'closed';
      }

      return 'other';
    };

    const incrementStatusCounter = (target: any, statusKey: keyof typeof statusTotals): void => {
      const counterKey = `${statusKey}_count`;
      target[counterKey] = Number(target[counterKey] || 0) + 1;
    };

    try {
      let incrementalContext = null;
      let processedRows = 0;
      let totalItems = 0;
      const exportPollingResult = await this.fetchExportPages(
        observationFilterCopy,
        exportPerPage,
        async (rows, meta) => {
          totalItems = meta?.total_items || totalItems;

          rows.forEach((item) => {
            const statusKey = normalizeStatusKey(item?.status);
            statusTotals[statusKey] += 1;

            const creatorName =
              `${item?.created_by || ''}`.trim() ||
              this.translateService.translate('Unknown', {}, lang);
            if (!userStatusMap.has(creatorName)) {
              userStatusMap.set(creatorName, {
                user_name: creatorName,
                pending_count: 0,
                in_progress_count: 0,
                completed_count: 0,
                closed_count: 0,
                other_count: 0,
              });
            }
            incrementStatusCounter(userStatusMap.get(creatorName), statusKey);

            const rawCategories = Array.isArray(item?.categories)
              ? item.categories
              : [item?.categories];
            const normalizedCategories: string[] = Array.from(
              new Set<string>(
                rawCategories
                  .map((category) => `${category || ''}`.trim())
                  .filter((category) => category.length > 0),
              ),
            );

            normalizedCategories.forEach((categoryName) => {
              if (!categoryStatusMap.has(categoryName)) {
                categoryStatusMap.set(categoryName, {
                  category_name: categoryName,
                  pending_count: 0,
                  in_progress_count: 0,
                  completed_count: 0,
                  closed_count: 0,
                  other_count: 0,
                });
              }
              incrementStatusCounter(categoryStatusMap.get(categoryName), statusKey);
            });
          });

          const normalizedRows = rows.map((item) => {
            const row = { ...item };
            if (includeImages) {
              row.Image = row.screen_shot;
            }
            delete row.screen_shot;
            delete row.deleted_at;
            row.due_date = GStatic.transformDate(
              row.due_date,
              ' YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
              false,
              true,
            );
            row.created_at = GStatic.transformDate(
              row.created_at,
              ' YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
              false,
              true,
            );
            row.user_mention = Array.isArray(row.user_mention)
              ? row.user_mention.join()
              : row.user_mention;
            row.categories = Array.isArray(row.categories)
              ? this.translateService.translate(row.categories).join()
              : row.categories;
            return row;
          });

          if (!normalizedRows.length) {
            this.exportProgress$.next({
              active: true,
              currentPage: meta?.current_page || 0,
              totalPages: meta?.total_pages || null,
              processedRows,
              totalItems: totalItems || null,
              cappedByTestMode: false,
            });
            return;
          }

          const translatedRows = this.translateKeys(normalizedRows, lang);
          const translatedRowsArray = Array.isArray(translatedRows)
            ? translatedRows
            : [translatedRows];

          if (!incrementalContext) {
            const headers = Object.keys(translatedRowsArray[0]);
            incrementalContext = this.xlsxService.createIncrementalWorksheetContext(
              headers,
              observationsSheetName,
              type,
            );
          }

          await this.xlsxService.appendIncrementalRows(
            incrementalContext,
            translatedRowsArray,
            includeImages && type === 'xlsx',
          );

          processedRows += translatedRowsArray.length;
          this.exportProgress$.next({
            active: true,
            currentPage: meta?.current_page || 0,
            totalPages: meta?.total_pages || null,
            processedRows,
            totalItems: totalItems || null,
            cappedByTestMode: false,
          });
        },
      );

      if (!incrementalContext) {
        this._toaster.warning(this.translateService.translate('No data found'));
        return;
      }

      const statusKey = this.translateService.translate('Status', {}, lang);
      const countKey = this.translateService.translate('count', {}, lang);
      const totalCount =
        statusTotals.pending +
        statusTotals.in_progress +
        statusTotals.completed +
        statusTotals.closed +
        statusTotals.other;
      const observationsStatus = [
        {
          [statusKey]: this.translateService.translate('Pending Count', {}, lang),
          [countKey]: statusTotals.pending,
        },
        {
          [statusKey]: this.translateService.translate('In Progress Count', {}, lang),
          [countKey]: statusTotals.in_progress,
        },
        {
          [statusKey]: this.translateService.translate('Completed Count', {}, lang),
          [countKey]: statusTotals.completed,
        },
        {
          [statusKey]: this.translateService.translate('Closed Count', {}, lang),
          [countKey]: statusTotals.closed,
        },
        // Only shown when the export actually contains integration-specific statuses, so the
        // breakdown stays as-is for clients that only use the 4 core ones.
        ...(statusTotals.other > 0
          ? [
              {
                [statusKey]: this.translateService.translate('Other Statuses Count', {}, lang),
                [countKey]: statusTotals.other,
              },
            ]
          : []),
        {
          [statusKey]: this.translateService.translate('Total Count', {}, lang),
          [countKey]: totalCount,
        },
      ];
      const dropEmptyOtherColumn = (row: any) => {
        if (statusTotals.other > 0) {
          return row;
        }
        const { other_count, ...rest } = row;
        return rest;
      };
      const observationsStatusByCategory = Array.from(categoryStatusMap.values())
        .sort((a, b) => `${a.category_name}`.localeCompare(`${b.category_name}`))
        .map(dropEmptyOtherColumn);
      const observationsStatusByUsers = Array.from(userStatusMap.values())
        .sort((a, b) => `${a.user_name}`.localeCompare(`${b.user_name}`))
        .map(dropEmptyOtherColumn);

      const extraSheets =
        type === 'xlsx'
          ? {
              [this.translateService.translate('Observations Status', {}, lang)]:
                this.translateKeys(observationsStatus, lang),
              [this.translateService.translate('Observations Status By Category', {}, lang)]:
                this.translateKeys(observationsStatusByCategory, lang),
              [this.translateService.translate('Observations Status By Users', {}, lang)]:
                this.translateKeys(observationsStatusByUsers, lang),
            }
          : {};

      await this.xlsxService.finalizeIncrementalWorkbook(
        incrementalContext,
        exportFileName,
        extraSheets,
      );
    } catch (error: any) {
      this._toaster.warning(
        error?.error?.message || error?.message || this.translateService.translate('Export failed'),
      );
    } finally {
      this.isLoading = false;
      this.exportProgress$.next({
        active: false,
        currentPage: 0,
        totalPages: null,
        processedRows: 0,
        totalItems: null,
        cappedByTestMode: false,
      });
    }
  }

  translateKeys(data: any, lang: string): any {
    if (Array.isArray(data)) {
      // If the data is an array, recursively translate each element in the array
      return data.map((element) => this.translateKeys(element, lang));
    } else if (typeof data === 'object') {
      // If the data is an object, recursively translate each key and value in the object
      const translatedObj = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          let value = data[key];

          if (key === 'categories' && typeof value === 'string') {
            // If the key is "categories" and the value is a string, translate the value
            value = this.translateService.translate(value, {}, lang);
          } else {
            // Otherwise, recursively translate the value
            value = this.translateKeys(value, lang);
          }
          // Translate the key
          const translatedKey = this.translateService.translate(key, {}, lang);
          translatedObj[translatedKey] = value;
        }
      }
      return translatedObj;
    } else {
      // If the data is neither an array nor an object, return it as-is
      return data;
    }
  }

  resetObservationPaginatorPageIndex() {
    this._pageIndex.next(0);
  }

  // Region: Private Methods

  // Region: Helper Methods

  // Region: Permission Methods
}
